<template>
  <div
      class="observation-editor"
      :class="{ 'observation-editor--number': observationType === 'number' || input === 'blood_pressure' }"
  >
    <template v-if="input === 'blood_pressure'">
      <div class="observation-editor__container">
        <blood-pressure-input
          :value="normalizedValue"
          :input-id="`evidence-${itemId}`"
          :label="label"
          :ranges="bloodPressureOptions"
          :unit="bloodPressureOptions ? bloodPressureOptions.unit : 'mmHg'"
          :systolic-label="$t('questions.blood_pressure.input_label.systolic')"
          :diastolic-label="$t('questions.blood_pressure.input_label.diastolic')"
          :systolic-invalid="bloodPressurePartInvalid('systolic')"
          :diastolic-invalid="bloodPressurePartInvalid('diastolic')"
          :systolic-warning="bloodPressurePartWarning('systolic')"
          :diastolic-warning="bloodPressurePartWarning('diastolic')"
          @input="handleBloodPressureInput"
        />
        <div
          v-if="validationMessage"
          class="observation-editor__error"
          :class="{ 'observation-editor__error--warning': isWarningMessage }"
          role="alert"
        >
          {{ $t(validationMessage.token, validationMessage.params) }}
        </div>
      </div>
    </template>

    <template v-else-if="observationType === 'option' && responseOptions.length > 0">
      <button
          v-for="opt in responseOptions"
          :key="opt.value"
          type="button"
          class="observation-editor__btn"
          :class="{ 'observation-editor__btn--active': normalizedValue === String(opt.value) }"
          :title="$t(opt.token)"
          @click="select(opt.value)"
      >
        {{ $t(opt.token) }}
      </button>
    </template>

    <template v-else-if="observationType === 'number'">
      <div class="observation-editor__number-field">
        <div class="observation-editor__container">
          <input
              type="number"
              class="observation-editor__input"
              :class="{
                'observation-editor__input--error': validationMessage,
                'observation-editor__input--warning': isWarningMessage,
              }"
              :value="numericInputValue"
              :min="numericOptions ? numericOptions.min : undefined"
              :max="numericOptions ? numericOptions.max : undefined"
              :step="numberStep"
              @input="handleInput"
          />
          <div
            v-if="validationMessage"
            class="observation-editor__error"
            :class="{ 'observation-editor__error--warning': isWarningMessage }"
            role="alert"
          >
            {{ $t(validationMessage.token, validationMessage.params) }}
          </div>
        </div>

        <button
            v-if="numericOptions && numericOptions.allowUnknown"
            type="button"
            class="observation-editor__btn observation-editor__btn--unknown-toggle"
            :class="{ 'observation-editor__btn--active': isUnknownActive }"
            @click="selectUnknown"
        >
          {{ $t('app.assessment_mode.text.input.option.value.unknown') }}
        </button>
      </div>
    </template>

    <template v-else-if="observationType === 'date'">
      <input
          type="date"
          class="observation-editor__input"
          :value="currentValue"
          @input="handleInput"
      />
    </template>

    <template v-else>
      <input
          type="text"
          class="observation-editor__input"
          :value="currentValue"
          @input="handleInput"
      />
    </template>

    <button
        v-if="canRemove"
        type="button"
        class="observation-editor__btn observation-editor__btn--incorrect"
        :class="{ 'observation-editor__btn--active observation-editor__btn--incorrect-active': markedIncorrect }"
        :title="$t('app.observation.button.remove.title')"
        @click="remove"
    >
      {{ $t('app.observation.button.remove.title') }}
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import BloodPressureInput from '@/components/assessment-inputs/BloodPressureInput.vue';
import type {
  BloodPressureOptions,
  DraftValidationError,
  NumericOptions,
  ObservationInputType,
  ObservationType,
  ResponseOption,
} from '../../types/text-mode.types';
import { useDraftStore } from '../../stores/draft.store';

type EvidenceValue = string | number | boolean | null;

export default defineComponent({
  name: 'ObservationEditor',

  components: {
    BloodPressureInput,
  },

  props: {
    itemId: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    observationType: {
      type: String as PropType<ObservationType>,
      required: true,
    },
    currentValue: {
      type: [String, Number, Boolean, null] as unknown as PropType<EvidenceValue>,
      default: null,
    },
    responseOptions: {
      type: Array as PropType<ResponseOption[]>,
      default: () => [],
    },
    numericOptions: {
      type: Object as PropType<NumericOptions | null>,
      default: null,
    },
    input: {
      type: String as PropType<ObservationInputType | undefined>,
      default: undefined,
    },
    bloodPressureOptions: {
      type: Object as PropType<BloodPressureOptions | null>,
      default: null,
    },
    markedIncorrect: {
      type: Boolean,
      default: false,
    },
    drafted: {
      type: Boolean,
      default: false,
    },
    canRemove: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const draftStore = useDraftStore();
    const normalizedValue = computed(() => String(props.currentValue ?? ''));

    const numericInputValue = computed((): string | number => {
      if (typeof props.currentValue === 'number') return props.currentValue;
      if (typeof props.currentValue !== 'string') return '';

      const directNumber = Number(props.currentValue);
      if (!Number.isNaN(directNumber)) return props.currentValue;

      const match = /^-?\d+(?:\.\d+)?/.exec(props.currentValue.trim());
      return match ? match[0] : '';
    });

    const numberStep = computed((): string => {
      const decimalPlaces = props.numericOptions?.decimalPlaces ?? 0;
      if (decimalPlaces <= 0) return '1';
      return `0.${'0'.repeat(decimalPlaces - 1)}1`;
    });

    const validationMessage = computed((): DraftValidationError | null => {
      if (props.markedIncorrect) return null;
      const validation = draftStore.validationFor(props.itemId);
      if (!validation || validation.severity === 'normal') return null;
      if (validation.severity === 'warning' && !draftStore.isWarningPresented(props.itemId)) {
        return null;
      }
      return draftStore.validationErrors[props.itemId]
        ?? (validation.messageToken ? { token: validation.messageToken } : null);
    });
    const isWarningMessage = computed(() => {
      const validation = draftStore.validationFor(props.itemId);
      return validationMessage.value !== null
        && validation?.severity === 'warning';
    });

    const isUnknownActive = computed((): boolean => {
      if (props.observationType !== 'number') return false;
      return typeof props.currentValue === 'string' && props.currentValue.trim().toLowerCase() === 'unknown';
    });

    const selectUnknown = (): void => {
      emit('value-change', { itemId: props.itemId, value: 'Unknown' });
    };

    const select = (value: string): void => {
      emit('value-change', { itemId: props.itemId, value });
    };

    const handleInput = (event: Event): void => {
      const value = (event.target as HTMLInputElement).value;
      emit('value-change', { itemId: props.itemId, value });
    };

    const handleBloodPressureInput = (value: string): void => {
      emit('value-change', { itemId: props.itemId, value });
    };

    const bloodPressurePartInvalid = (part: 'systolic' | 'diastolic'): boolean => {
      const validation = draftStore.validationFor(props.itemId);
      if (!validation || validation.severity === 'warning') return false;
      if (validation.code === 'invalid_relationship' || validation.code === 'invalid_format'
        || validation.code === 'invalid_configuration') return true;
      return validation.parts?.[part]?.severity === 'blocking';
    };

    const bloodPressurePartWarning = (part: 'systolic' | 'diastolic'): boolean => {
      if (!isWarningMessage.value) return false;
      return draftStore.validationFor(props.itemId)?.parts?.[part]?.severity === 'warning';
    };

    const remove = (): void => {
      emit('remove', props.itemId);
    };

    return {
      normalizedValue,
      numericInputValue,
      validationMessage,
      isWarningMessage,
      numberStep,
      isUnknownActive,
      selectUnknown,
      select,
      handleInput,
      handleBloodPressureInput,
      bloodPressurePartInvalid,
      bloodPressurePartWarning,
      remove,
    };
  },
});
</script>
